import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Button, message } from "antd";
import ProductServ from "../service/ProductServ";
import ProductTagServ from "../service/ProductTagServ";
import GlobalButton from "../util/GlobalButton";
import SafeImage from "./SafeImage";
import { Row, Col } from "antd";

const { Option } = Select;

const AddProductTag = ({ open, refreshTags, tagDetails, isEditing }) => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // ✅ Fetch products for dropdown
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await ProductServ.index();
        // Handle both direct array or paginated object response
        const productList = Array.isArray(response) ? response : (response?.data || []);
        setProducts(productList);
      } catch (error) {
        console.error("Error loading products:", error);
      }
    };
    fetchProducts();
  }, []);

  // ✅ Set form values if editing
  useEffect(() => {
    if (isEditing && tagDetails) {
      // productId may be a populated object { _id, name, imageUrl } OR just a string ID
      const productIdValue = tagDetails.productId?._id || tagDetails.productId;

      const prod = products.find(p => (p.id || p._id) === productIdValue || (p._id?.toString() === productIdValue?.toString())) || null;
      setSelectedProduct(prod);
      form.setFieldsValue({
        productId: productIdValue,
        tag: tagDetails.tag,
      });
    } else {
      form.resetFields();
      setSelectedProduct(null);
    }
  }, [tagDetails, isEditing, form, products]);

  // ✅ Submit form
  const onFinish = async (values) => {
    try {
      setSubmitting(true);

      const prod = products.find((p) => (p.id || p._id) === values.productId);
      if (!prod) {
        message.error("Please select a valid product!");
        return;
      }

      // Payload without sending null product
      const payload = {
        productId: values.productId,
        tag: values.tag,
      };

      if (isEditing && (tagDetails?.id || tagDetails?._id)) {
        const tagId = tagDetails._id || tagDetails.id;
        await ProductTagServ.update(tagId, payload);
        message.success("Tag updated successfully!");
      } else {
        await ProductTagServ.create(payload);
        message.success("Tag added successfully!");
      }

      handleClose(true);
    } catch (err) {
      console.error(err);
      message.error("Error saving tag!");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (shouldRefresh) => {
    refreshTags(shouldRefresh);
    form.resetFields();
    setSelectedProduct(null);
  };

  return (
    <Modal
      title={isEditing ? "Edit Product Tag" : "Add Product Tag"}
      open={open}
      onCancel={() => handleClose(false)}
      footer={[
        <div className="form-footer-btn">
          <GlobalButton
            action="cancel"
            key="cancel"
            onClick={() => handleClose(false)}
            disabled={submitting}
          >
            Cancel
          </GlobalButton>
          <GlobalButton
            action="submit"
            key="submit"
            onClick={() => form.submit()}
            loading={submitting}
          >
            Submit
          </GlobalButton>
        </div>
      ]}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label="Product"
          name="productId"
          rules={[{ required: true, message: "Please select a product" }]}
        >
          <Select
            placeholder="Select product"
            optionLabelProp="label"
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(value) => {
              const prod = products.find((p) => (p.id || p._id) === value);
              setSelectedProduct(prod);
            }}
          >
            {products.map((p) => {
              const pid = p.id || p._id;
              return (
                <Option key={pid} value={pid} label={p.name}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <SafeImage
                      src={p.imageUrl}
                      alt={p.name}
                      variant="thumbnail"
                      style={{ width: '32px', height: '32px', flexShrink: 0 }}
                    />
                    <span>{p.name}</span>
                  </div>
                </Option>
              );
            })}
          </Select>
        </Form.Item>

        {/* Display selected product info */}
        {selectedProduct && (
          <div className="pcu-margin-bottom-16 pcu-padding-12 pcu-bg-muted-light pcu-radius-12 pcu-border-light">
            <Row gutter={16} align="middle">
              <Col>
                <SafeImage 
                  src={selectedProduct.imageUrl} 
                  alt={selectedProduct.name} 
                  variant="preview"
                  style={{ maxHeight: '100px', width: 'auto' }}
                />
              </Col>
              <Col>
                <div className="pcu-font-14 pcu-weight-600">{selectedProduct.name}</div>
                <div className="pcu-color-muted pcu-font-12">ID: {selectedProduct.id || selectedProduct._id}</div>
              </Col>
            </Row>
          </div>
        )}

        <Form.Item
          label="Tag"
          name="tag"
          rules={[{ required: true, message: "Please enter tag" }]}
        >
          <Input placeholder="E.g., New Arrival / Discount / Trending" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddProductTag;
